import React, { FormEvent, useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { CrmShell, PageHeader, Button } from '../../components/CrmShell';
import { Field, inputClass } from '../../components/CrmForms';
import { savePipelineStages, subscribeToPipelineSettings } from '../../services/crm';
import { logGlobalError } from '../../utils/globalErrorLogger';

export function SettingsPage() {
  const [stages, setStages] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => subscribeToPipelineSettings((settings) => setStages(settings.stages), (err) => setError(err.message)), []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await savePipelineStages(stages);
      setSaved(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to save settings');
      logGlobalError(err, { context: 'SettingsPage.savePipelineStages' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <CrmShell>
      <PageHeader title="Settings" description="Configure V1 CRM defaults." />
      <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900">Pipeline stages</h2>
        <form onSubmit={submit} className="mt-4 space-y-3">
          {stages.map((stage, index) => (
            <div key={index} className="flex items-end gap-2">
              <div className="flex-1">
                <Field label={`Stage ${index + 1}`}>
                  <input
                    className={inputClass}
                    value={stage}
                    onChange={(event) => setStages(stages.map((item, itemIndex) => itemIndex === index ? event.target.value : item))}
                    required
                  />
                </Field>
              </div>
              <Button
                type="button"
                variant="secondary"
                className="px-2"
                title="Remove stage"
                onClick={() => setStages(stages.filter((_, itemIndex) => itemIndex !== index))}
                disabled={stages.length <= 1}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="flex flex-wrap gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setStages([...stages, 'New Stage'])}>
              <Plus className="mr-1 inline h-4 w-4" />Add stage
            </Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save settings'}</Button>
          </div>
        </form>
        {saved && <p className="mt-3 text-sm text-green-700">Settings saved.</p>}
        {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
      </section>
    </CrmShell>
  );
}
